import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  SwipeableDrawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Typography,
  Box,
  Collapse,
  IconButton,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Business as AdminIcon,
  Settings as SettingsIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Close as CloseIcon,
  People as CustomersIcon,
  Work as ProjectsIcon,
  AccessTime as TimecardsIcon,
  Person as UsersIcon,
  Group as TeamsIcon,
  Settings as SystemSettingsIcon,
  Create as CreateIcon,
  Edit as EditIcon,
  Folder as ResourcesIcon,
  ThumbUp as ApprovalIcon,
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';

const MobileDrawerMenu = ({ open, onOpen, onClose }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [openMenus, setOpenMenus] = React.useState({
    administration: false,
    system: false,
    timecards: false,
  });

  // Don't render on desktop
  if (!isMobile) return null;

  const isAdmin = user?.role === 'admin';
  const isTeamLead = user?.role === 'team_lead';

  const handleMenuClick = (menu) => {
    setOpenMenus((prev) => ({
      ...prev,
      [menu]: !prev[menu],
    }));
  };

  const handleNavigation = (path) => {
    navigate(path);
    onClose();
  };

  const menuItems = [];

  // Team lead has Hours Approval
  if (isTeamLead) {
    menuItems.push({
      title: 'Hours Approval',
      path: '/hours-approval',
      icon: <ApprovalIcon sx={{ color: '#2196f3' }} />,
    });
  }

  // Admin and team lead have Administration dropdown
  if (isAdmin || isTeamLead) {
    menuItems.push({
      id: 'administration',
      title: 'Administration',
      icon: <AdminIcon sx={{ color: '#2196f3' }} />,
      submenu: [
        { 
          title: 'Customers', 
          path: '/customers',
          icon: <CustomersIcon />
        },
        { 
          title: 'Projects', 
          path: '/projects',
          icon: <ProjectsIcon />
        },
        { 
          id: 'timecards',
          title: 'Timecards',
          icon: <TimecardsIcon sx={{ color: '#2196f3' }} />,
          submenu: [
            { 
              title: 'Create Timecards', 
              path: '/create-timecards',
              icon: <CreateIcon />
            },
            { 
              title: 'Edit Timecards', 
              path: '/edit-timecards',
              icon: <EditIcon />
            }
          ]
        },
      ],
    });
  }

  // Admin and team lead have System dropdown
  if (isAdmin || isTeamLead) {
    menuItems.push({
      id: 'system',
      title: 'System',
      icon: <SettingsIcon sx={{ color: '#2196f3' }} />,
      submenu: [
        { 
          title: 'Users', 
          path: '/users',
          icon: <UsersIcon />
        },
        { 
          title: 'Teams', 
          path: '/teams',
          icon: <TeamsIcon />
        },
        { 
          title: 'Settings', 
          path: '/settings',
          icon: <SystemSettingsIcon />
        },
      ],
    });
  }

  return (
    <SwipeableDrawer
      anchor="right"
      open={open}
      onClose={onClose}
      onOpen={onOpen}
      sx={{
        '& .MuiDrawer-paper': {
          width: 280,
          maxWidth: '80vw',
        },
      }}
    >
      <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h6" component="div">
          Menu
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon sx={{ color: '#2196f3' }} />
        </IconButton>
      </Box>
      <Divider />
      
      <List sx={{ pt: 0 }}>
        {menuItems.map((item) => (
          <React.Fragment key={item.title}>
            {item.submenu ? (
              <ListItem
                button
                onClick={() => handleMenuClick(item.id)}
                sx={{
                  backgroundColor: item.submenu?.some(sub => 
                    location.pathname === sub.path || 
                    sub.submenu?.some(nested => location.pathname === nested.path)
                  ) ? 'action.selected' : 'transparent',
                }}
              >
                <ListItemIcon>{item.icon}</ListItemIcon>
                <ListItemText primary={item.title} />
                {openMenus[item.id] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </ListItem>
            ) : (
              <ListItem
                button
                onClick={() => handleNavigation(item.path)}
                sx={{
                  backgroundColor: location.pathname === item.path ? 
                    'primary.main' : 'transparent',
                  color: location.pathname === item.path ? 
                    'primary.contrastText' : 'inherit',
                  '&:hover': {
                    backgroundColor: location.pathname === item.path ? 
                      'primary.dark' : 'action.hover',
                  },
                }}
              >
                <ListItemIcon 
                  sx={{ 
                    color: location.pathname === item.path ? 
                      'primary.contrastText' : 'inherit' 
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                <ListItemText primary={item.title} />
              </ListItem>
            )}
            
            {item.submenu && (
              <Collapse in={openMenus[item.id]} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  {item.submenu.map((subItem) => (
                  <React.Fragment key={subItem.title}>
                    {subItem.submenu ? (
                      <>
                        <ListItem
                          button
                          onClick={() => handleMenuClick(subItem.id)}
                          sx={{ 
                            pl: 4,
                            backgroundColor: subItem.submenu.some(nested => 
                              location.pathname === nested.path
                            ) ? 'action.selected' : 'transparent',
                          }}
                        >
                          <ListItemIcon>{subItem.icon}</ListItemIcon>
                          <ListItemText primary={subItem.title} />
                          {openMenus[subItem.id] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </ListItem>
                        
                        <Collapse in={openMenus[subItem.id]} timeout="auto" unmountOnExit>
                          <List component="div" disablePadding>
                            {subItem.submenu.map((nestedItem) => (
                              <ListItem
                                key={nestedItem.title}
                                button
                                onClick={() => handleNavigation(nestedItem.path)}
                                sx={{ 
                                  pl: 6,
                                  backgroundColor: location.pathname === nestedItem.path ? 
                                    'primary.main' : 'transparent',
                                  color: location.pathname === nestedItem.path ? 
                                    'primary.contrastText' : 'inherit',
                                  '&:hover': {
                                    backgroundColor: location.pathname === nestedItem.path ? 
                                      'primary.dark' : 'action.hover',
                                  },
                                }}
                              >
                                <ListItemIcon 
                                  sx={{ 
                                    color: location.pathname === nestedItem.path ? 
                                      'primary.contrastText' : 'inherit' 
                                  }}
                                >
                                  {nestedItem.icon}
                                </ListItemIcon>
                                <ListItemText primary={nestedItem.title} />
                              </ListItem>
                            ))}
                          </List>
                        </Collapse>
                      </>
                    ) : (
                      <ListItem
                        button
                        onClick={() => handleNavigation(subItem.path)}
                        sx={{ 
                          pl: 4,
                          backgroundColor: location.pathname === subItem.path ? 
                            'primary.main' : 'transparent',
                          color: location.pathname === subItem.path ? 
                            'primary.contrastText' : 'inherit',
                          '&:hover': {
                            backgroundColor: location.pathname === subItem.path ? 
                              'primary.dark' : 'action.hover',
                          },
                        }}
                      >
                        <ListItemIcon 
                          sx={{ 
                            color: location.pathname === subItem.path ? 
                              'primary.contrastText' : 'inherit' 
                          }}
                        >
                          {subItem.icon}
                        </ListItemIcon>
                        <ListItemText primary={subItem.title} />
                      </ListItem>
                    )}
                  </React.Fragment>
                  ))}
                </List>
              </Collapse>
            )}
          </React.Fragment>
        ))}
      </List>
    </SwipeableDrawer>
  );
};

export default MobileDrawerMenu;
